/**
 * Mia Platform OAuth 2.0 Authentication Service
 * Supports both Okta and Mia MCP OAuth based on OKTA_AUTH flag
 */
import config, {getHubConfig} from '../config.js';
import { getBffHeaders, getBeHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

class MiaPlatformAuth {
  constructor() {
    const hubConfig = getHubConfig();
    
    // Check if using Okta or Mia MCP auth from hubConfig
    this.useOktaAuth = hubConfig.miaPlatform?.useOktaAuth || false;
    
    // Debug: Log configuration
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('🔍 MiaPlatformAuth Configuration:', {
        oktaAuth: hubConfig.oktaAuth,
        useOktaAuth: this.useOktaAuth,
        willUse: this.useOktaAuth ? 'Okta Direct' : 'Mia MCP OAuth',
        authMode: this.useOktaAuth ? '⚠️ OKTA MODE - USING OKTA' : '✅ MCP MODE - USING MIA MCP'
      });
    }
    
    // Client configuration - use MCP OAuth by default
    if (this.useOktaAuth) {
      // Okta OAuth (legacy/alternative flow)
      this.scope = 'openid profile email offline_access groups';
    } else {
      // Mia MCP OAuth (recommended flow - matches console-mcp-server example)
      this.scope = 'profile email openid offline-access';
    }
    
    // Use the frontend URL from config to build redirect URI
    // This ensures the callback comes back to the correct location
    const frontendUrl = config.frontendUrl || window.location.origin;
    this.redirectUri = `${frontendUrl}`;
    this.tokenKey = 'mia_platform_oauth_token';
    this.refreshTokenKey = 'mia_platform_refresh_token';
    this.userInfoKey = 'mia_platform_user_info';
    
    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('🏗️ MiaPlatformAuth initialized:', {
        authMode: this.useOktaAuth ? 'okta' : 'mia-mcp',
        redirectUri: this.redirectUri,
        authorizationUrl: this.authorizationUrl,
        frontendUrl
      });
    }
  }

  get baseUrl() {
    return config.consoleBaseUrl;
  }

  get authorizationUrl() {
    if (this.useOktaAuth) {
      return 'https://login.mia-platform.eu/oauth2/v1/authorize';
    }
    return `${this.baseUrl}/console-mcp-server/oauth/authorize`;
  }


  /**
   * Register a new OAuth client for Mia MCP flow
   */
  async registerClient() {
    try {
      // IMPORTANT: redirect_uri must match exactly what we'll use in auth flow
      const redirectUri = this.redirectUri;
      
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Step 1: Registering OAuth client...');
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Registration details:', {
        redirect_uri: redirectUri,
        grant_types: ['authorization_code', 'refresh_token'],
        scope: this.scope,
        endpoint: `${config.bffUrl}/bff/auth/mia-platform/register`
      });
      
      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/auth/mia-platform/register`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          redirect_uris: [redirectUri],
          grant_types: ['authorization_code', 'refresh_token'],
          response_types: ['code'],
          client_name: 'Mia Flow',
          token_endpoint_auth_method: 'client_secret_post',
          scope: this.scope,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // eslint-disable-next-line no-console
        console.error('❌ [MIA MCP] Client registration failed:', errorData);
        throw new Error(errorData.error || 'Client registration failed');
      }

      const clientData = await response.json();
      
      // Store client credentials - use localStorage to survive OAuth redirect
      // These will be cleaned up after successful token exchange
      localStorage.setItem('mia_mcp_client_id', clientData.client_id);
      localStorage.setItem('mia_mcp_client_secret', clientData.client_secret);
      localStorage.setItem('mia_mcp_client_expires', clientData.client_secret_expires_at || '0');
      
      // eslint-disable-next-line no-console
      console.log('✅ [MIA MCP] Client registered successfully:', {
        client_id: clientData.client_id,
        expires_at: clientData.client_secret_expires_at,
        redirect_uris: clientData.redirect_uris
      });
      
      return clientData;
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('🔴 MCP client registration error:', error);
      throw error;
    }
  }

  /**
   * Get the OAuth authorization URL for external browser flow
   */
  getAuthUrl(state, codeChallenge, clientId) {
    // MCP flow uses the dynamically-registered client ID passed in.
    // (The Okta flow is not wired client-side.)
    const effectiveClientId = this.useOktaAuth ? undefined : clientId;

    if (!effectiveClientId) {
      throw new Error('Client ID is required');
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: effectiveClientId,
      scope: this.scope,
      redirect_uri: this.redirectUri,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });
    
    // Add audience parameter for Okta
    if (this.useOktaAuth) {
      params.set('audience', `${this.baseUrl}/console-mcp-server/mcp`);
    }

    return `${this.authorizationUrl}?${params.toString()}`;
  }

  /**
   * Generate PKCE parameters (code verifier and challenge)
   */
  generatePKCE() {
    const codeVerifier = this.generateRandomString(128);
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))
      .then(hash => {
        // Convert hash to base64url properly
        const hashArray = new Uint8Array(hash);
        let binaryString = '';
        for (let i = 0; i < hashArray.length; i++) {
          binaryString += String.fromCharCode(hashArray[i]);
        }
        const codeChallenge = btoa(binaryString)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=/g, '');
        return {codeVerifier, codeChallenge};
      });
  }

  /**
   * Generate a random string for PKCE
   */
  generateRandomString(length) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  }

  /**
   * Start the OAuth authentication flow using frontend redirect
   */
  async startAuthFlow() {
    try {
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Starting OAuth authentication flow...');
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Auth mode:', this.useOktaAuth ? 'Okta' : 'Mia MCP');
      
      let clientId;

      // For MCP flow, register a new client first
      if (!this.useOktaAuth) {
        const clientData = await this.registerClient();
        clientId = clientData.client_id;
      } else {
        // Okta flow is not wired client-side
        throw new Error('Okta auth flow is not configured.');
      }

      // Get authorization URL from BFF (passing redirect_uri and client_id for MCP flow)
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Step 2: Getting authorization URL from BFF...');
      const loginUrl = `${config.bffUrl}/bff/auth/mia-platform/login?redirect_uri=${encodeURIComponent(this.redirectUri)}&client_id=${encodeURIComponent(clientId)}`;
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Login request:', {
        url: loginUrl,
        redirect_uri: this.redirectUri,
        client_id: clientId
      });
      
      const authUrlResponse = await getAuthorizedFetch()(loginUrl, {
        method: 'GET',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!authUrlResponse.ok) {
        const errorData = await authUrlResponse.text();
        // eslint-disable-next-line no-console
        console.error('❌ [MIA MCP] Failed to get authorization URL:', errorData);
        throw new Error('Failed to get authorization URL from BFF');
      }

      const {authUrl} = await authUrlResponse.json();
      
      // eslint-disable-next-line no-console
      console.log('✅ [MIA MCP] Authorization URL received');
      
      // Extract state from the authorization URL (generated by BFF)
      const state = new URL(authUrl).searchParams.get('state');
      
      // Store state for validation during callback
      // Note: BFF already generated and stored the code_verifier in its session
      // We don't need to generate one on the frontend
      sessionStorage.setItem('mia_platform_auth_state', state);
      
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Step 3: Redirecting to authorization server...');
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Authorization details:', {
        state,
        auth_url: authUrl,
        note: 'BFF manages PKCE code_verifier'
      });
      
      // Redirect to auth provider
      window.location.href = authUrl;
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('🔴 MiaPlatformAuth startAuthFlow error:', error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for tokens (called from callback route)
   */
  async exchangeCodeForToken(code, state) {
    try {
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Step 4: Exchanging authorization code for tokens...');
      
      const storedState = sessionStorage.getItem('mia_platform_auth_state');
      
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Token exchange validation:', {
        received_state: state,
        stored_state: storedState,
        state_match: storedState === state,
        has_code: !!code,
        code_length: code?.length
      });
      
      if (!storedState || storedState !== state) {
        // eslint-disable-next-line no-console
        console.error('❌ [MIA MCP] State mismatch!', {storedState, receivedState: state});
        throw new Error('Invalid state parameter');
      }
      
      // Prepare token exchange request body
      // Note: BFF will use the code_verifier it stored in its session
      const tokenRequestBody = {
        code,
        state
      };
      
      // For MCP flow, include client credentials
      if (!this.useOktaAuth) {
        const clientId = localStorage.getItem('mia_mcp_client_id');
        const clientSecret = localStorage.getItem('mia_mcp_client_secret');
        
        // eslint-disable-next-line no-console
        console.log('� [MIA MCP] Client credentials check:', {
          has_client_id: !!clientId,
          has_client_secret: !!clientSecret,
          client_id: clientId ? `${clientId.substring(0, 10)}...` : 'MISSING'
        });
        
        if (!clientId || !clientSecret) {
          // eslint-disable-next-line no-console
          console.error('❌ [MIA MCP] Missing client credentials in localStorage');
          throw new Error('Missing MCP client credentials - please start a new login flow by clicking the login button again');
        }
        
        tokenRequestBody.client_id = clientId;
        tokenRequestBody.client_secret = clientSecret;
      }
      
      // eslint-disable-next-line no-console
      console.log('🔐 [MIA MCP] Sending token exchange request to BFF...');
      
      // Exchange code for tokens via BFF
      const response = await getAuthorizedFetch()(`${config.bffUrl}/bff/auth/mia-platform/token`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(tokenRequestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // eslint-disable-next-line no-console
        console.error('❌ [MIA MCP] Token exchange failed:', {
          status: response.status,
          error: errorData
        });
        throw new Error(errorData.error || 'Token exchange failed');
      }

      const tokens = await response.json();
      
      // eslint-disable-next-line no-console
      console.log('✅ [MIA MCP] Tokens received - full object keys:', Object.keys(tokens));
      // eslint-disable-next-line no-console
      console.log('✅ [MIA MCP] Tokens received successfully:', {
        has_access_token: !!tokens.access_token,
        has_refresh_token: !!tokens.refresh_token,
        has_id_token: !!tokens.id_token,
        id_token_preview: tokens.id_token ? `${tokens.id_token.substring(0, 20)}...` : 'MISSING',
        token_type: tokens.token_type,
        expires_in: tokens.expires_in,
        expires_at: tokens.expires_at
      });
      
      // Store tokens
      localStorage.setItem(this.tokenKey, tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem(this.refreshTokenKey, tokens.refresh_token);
      }
      if (tokens.id_token) {
        localStorage.setItem('mia_platform_id_token', tokens.id_token);
      }
      
      // Store expiration time (convert expires_in to timestamp if needed)
      if (tokens.expires_at) {
        localStorage.setItem('mia_platform_expires_at', tokens.expires_at.toString());
      } else if (tokens.expires_in) {
        const expiresAt = Date.now() + (tokens.expires_in * 1000);
        localStorage.setItem('mia_platform_expires_at', expiresAt.toString());
      }
      
      // eslint-disable-next-line no-console
      console.log('✅ [MIA MCP] Tokens stored in localStorage');
      
      // Clean up session storage
      sessionStorage.removeItem('mia_platform_auth_state');
      if (!this.useOktaAuth) {
        // Clean up MCP client credentials from localStorage
        localStorage.removeItem('mia_mcp_client_id');
        localStorage.removeItem('mia_mcp_client_secret');
        localStorage.removeItem('mia_mcp_client_expires');
      }
      
      // eslint-disable-next-line no-console
      console.log('✅ [MIA MCP] Cleaned up temporary OAuth data');
      
      // Get and store user info
      try {
        // eslint-disable-next-line no-console
        console.log('🔐 [MIA MCP] Fetching user info...');
        const userInfo = await this.getUserInfo();
        localStorage.setItem(this.userInfoKey, JSON.stringify(userInfo));
        // eslint-disable-next-line no-console
        console.log('✅ [MIA MCP] User info stored:', {
          email: userInfo.email,
          name: userInfo.name
        });
      } catch (userError) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ [MIA MCP] Failed to fetch user info:', userError);
      }
      
      // Save credentials to backend for MCP use via shared credential manager
      try {
        // eslint-disable-next-line no-console
        console.log('💾 [MIA MCP] Step 5: Saving credentials to backend for MCP tool use...');
        await this.saveCredentialsToBackend();
        // eslint-disable-next-line no-console
        console.log('✅ [MIA MCP] Credentials successfully saved to backend shared credential manager');
      } catch (backendError) {
        // eslint-disable-next-line no-console
        console.error('❌ [MIA MCP] Failed to save credentials to backend:', backendError);
        // eslint-disable-next-line no-console
        console.error('⚠️ [MIA MCP] WARNING: Frontend authentication succeeded but backend credential save failed!');
        // eslint-disable-next-line no-console
        console.error('⚠️ [MIA MCP] This means MCP tools will NOT work until credentials are manually saved.');
        // eslint-disable-next-line no-console
        console.error('⚠️ [MIA MCP] Please check that the backend is running and accessible.');
        // Store error info in sessionStorage so we can show a warning to the user
        sessionStorage.setItem('mia_platform_backend_save_error', backendError.message || 'Failed to save credentials to backend');
        // This is not a fatal error - the user is still authenticated for frontend use
      }
      
      // eslint-disable-next-line no-console
      console.log('🎉 [MIA MCP] OAuth flow completed successfully!');
      
      return tokens;
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('🔴 MiaPlatformAuth token exchange error:', error);
      throw error;
    }
  }

  /**
   * Get stored access token
   */
  getAccessToken() {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    // TODO: Implement token refresh logic
    // For now, just check if token exists without trying to refresh
    // The refresh endpoint needs to be implemented in the BFF first
    return true;

    // DISABLED: Token refresh logic (will be implemented later)
    // try {
    //   // Try to get user info to validate token
    //   await this.getUserInfo();
    //   return true;
    // } catch (error) {
    //   // Token might be expired, try refresh
    //   return this.refreshTokenIfNeeded();
    // }
  }

  /**
   * Get user information
   */
  async getUserInfo() {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    // For MCP mode, we need to send the ID token to decode user info
    const idToken = localStorage.getItem('mia_platform_id_token');
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    };
    
    // Add ID token header for MCP mode (BFF will decode it)
    if (!this.useOktaAuth && idToken) {
      headers['X-Id-Token'] = idToken;
    }

    const response = await fetch(`${config.bffUrl}/bff/auth/mia-platform/user`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, clear it
        this.clearTokens();
        throw new Error('Authentication expired');
      }
      const errorText = await response.text();
      // eslint-disable-next-line no-console
      console.error('❌ [MIA MCP] Get user info failed:', errorText);
      throw new Error(`Failed to get user info: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Refresh token if needed
   * TODO: Implement refresh token endpoint in BFF (/bff/auth/mia-platform/refresh)
   * This method is currently disabled to prevent 404 errors
   */
  async refreshTokenIfNeeded() {
    // TODO: Re-enable this once BFF refresh endpoint is implemented
    return false;
    
    /* DISABLED - uncomment once BFF endpoint is ready
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${config.bffUrl}/bff/auth/mia-platform/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const tokens = await response.json();
      
      // Update stored tokens
      localStorage.setItem(this.tokenKey, tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem(this.refreshTokenKey, tokens.refresh_token);
      }

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Token refresh failed:', error);
      this.clearTokens();
      return false;
    }
    */
  }

  /**
   * Clear all stored tokens and user data
   */
  clearTokens() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userInfoKey);
  }

  /**
   * Logout user
   */
  logout() {
    this.clearTokens();
    // Could also call logout endpoint if needed
  }

  /**
   * Get stored user info
   */
  getStoredUserInfo() {
    const stored = localStorage.getItem(this.userInfoKey);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Check backend credential status for MCP integration
   */
  async checkBackendCredentialStatus() {
    try {
      const response = await getAuthorizedFetch()(`${config.agentUrl}/api/mcp/mia-platform/status`, {
        method: 'GET',
        headers: getBeHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        return {
          authenticated: false,
          status: 'error',
          error: `HTTP ${response.status}`
        };
      }

      const result = await response.json();
      return {
        authenticated: result.success && result.connected,
        status: result.connected ? 'connected' : 'disconnected',
        error: result.error || null
      };
      
    } catch (error) {
      return {
        authenticated: false,
        status: 'error',
        error: error.message
      };
    }
  }

  /**
   * Save credentials to backend for MCP use via shared credential manager
   */
  async saveCredentialsToBackend() {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    
    if (!token) {
      throw new Error('No access token available');
    }

    try {
      // eslint-disable-next-line no-console
      console.log('🔄 Saving credentials to shared credential manager for MCP use...', {
        tokenLength: token.length,
        hasRefreshToken: !!refreshToken,
        endpoint: `${config.agentUrl}/api/mcp/mia-platform/credentials/save`
      });

      // Save to shared credential manager (same as Atlassian)
      const response = await fetch(`${config.agentUrl}/api/mcp/mia-platform/credentials/save`, {
        method: 'POST',
        headers: getBeHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          access_token: token,
          refresh_token: refreshToken,
          expires_in: 3600, // Default expiration
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // eslint-disable-next-line no-console
        console.error('❌ Failed to save credentials to shared credential manager:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Failed to save credentials: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      // eslint-disable-next-line no-console
      console.log('✅ Credentials saved to shared credential manager successfully:', result);
      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to save credentials to shared credential manager:', error);
      throw error;
    }
  }
}

// Export singleton instance
const miaPlatformAuth = new MiaPlatformAuth();
export default miaPlatformAuth;
